// Camera guard for proctored assessments.
//
// Problem: a candidate can pass the pre-assessment BiometricCheck and then turn
// the camera OFF (unplug, OS toggle, browser permission revoke, or stop the
// track via devtools). With no camera there is nothing to proctor, so the
// integrity report has no data — we want to STOP the assessment instead and
// force the camera back on.
//
// This hook continuously verifies a live camera track and reports cameraLost.
// It uses two independent signals because no single one is reliable across
// browsers:
//   1. track 'ended'/'mute' events (fire on unplug / OS revoke on most engines),
//   2. a periodic poll of the track's readyState/enabled/muted + stream.active
//      (covers programmatic stop() and engines that don't emit the events).
//
// Report-only proctoring stays untouched; this is a hard gate, gated by the
// caller to allowProctoring so non-proctored assessments are unaffected.
import { useCallback, useEffect, useRef, useState } from 'react'

const POLL_INTERVAL_MS = 1500
// A camera that's momentarily renegotiating shouldn't trip the guard. Require
// the track to look dead for two consecutive polls before we declare it lost.
const CONFIRM_POLLS = 2

// Is this MediaStream currently delivering live video?
export const isCameraLive = (stream) => {
  if (!stream || typeof stream.getVideoTracks !== 'function') return false
  const track = stream.getVideoTracks()[0]
  if (!track) return false
  // readyState 'ended' = stopped; muted/!enabled = no frames flowing.
  return track.readyState === 'live' && track.enabled && !track.muted
}

/**
 * @param {object}   opts
 * @param {boolean}  opts.enabled    only guard when proctoring is on AND the assessment is live
 * @param {function} opts.getStream  () => MediaStream | null  — the current proctor camera stream
 * @param {function} [opts.onLost]   called once when the camera is confirmed lost
 * @param {function} [opts.onRestored] called once when the camera comes back
 * @returns {{ cameraLost: boolean, recheck: function }}
 */
export default function useCameraGuard({ enabled, getStream, onLost, onRestored }) {
  const [cameraLost, setCameraLost] = useState(false)
  const lostRef = useRef(false)
  const deadPollsRef = useRef(0)
  const getStreamRef = useRef(getStream)
  const onLostRef = useRef(onLost)
  const onRestoredRef = useRef(onRestored)
  useEffect(() => { getStreamRef.current = getStream }, [getStream])
  useEffect(() => { onLostRef.current = onLost }, [onLost])
  useEffect(() => { onRestoredRef.current = onRestored }, [onRestored])

  const markLost = useCallback(() => {
    if (lostRef.current) return
    lostRef.current = true
    setCameraLost(true)
    if (typeof onLostRef.current === 'function') onLostRef.current()
  }, [])

  const markRestored = useCallback(() => {
    if (!lostRef.current) return
    lostRef.current = false
    deadPollsRef.current = 0
    setCameraLost(false)
    if (typeof onRestoredRef.current === 'function') onRestoredRef.current()
  }, [])

  // Imperative one-shot check — use at the Start gate so a passed BiometricCheck
  // plus a since-unplugged camera can't begin the assessment. Returns true if live.
  const recheck = useCallback(() => {
    const stream = typeof getStreamRef.current === 'function' ? getStreamRef.current() : null
    return isCameraLive(stream)
  }, [])

  useEffect(() => {
    if (!enabled) {
      // Not guarding (proctoring off or assessment not live) — reset state.
      lostRef.current = false
      deadPollsRef.current = 0
      setCameraLost(false)
      return undefined
    }

    let cancelled = false

    // Event-based: attach end/mute handlers to the current track.
    const attach = () => {
      const stream = typeof getStreamRef.current === 'function' ? getStreamRef.current() : null
      const track = stream && stream.getVideoTracks ? stream.getVideoTracks()[0] : null
      if (!track) return
      // 'ended' = track truly stopped (unplug / stop()) — always a real loss.
      const tripEnded = () => { if (!cancelled) markLost() }
      // 'mute' fires both on a real camera-off AND when the browser suspends the
      // track because the tab was backgrounded. A backgrounded tab is a tab-switch
      // (handled by the page's own tab-switch flow), not the camera being turned
      // off — so ignore mute while the page is hidden to avoid a false camera loss.
      const tripMute = () => {
        if (cancelled) return
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
        markLost()
      }
      track.addEventListener('ended', tripEnded)
      track.addEventListener('mute', tripMute)
      return () => {
        try { track.removeEventListener('ended', tripEnded) } catch (e) { /* noop */ }
        try { track.removeEventListener('mute', tripMute) } catch (e) { /* noop */ }
      }
    }
    let detach = attach()

    // Poll-based: the robust backstop. Re-attaches event handlers if the stream
    // object changed (re-acquired), and confirms loss/recovery over a few ticks.
    const id = setInterval(() => {
      if (cancelled) return
      // While the tab is backgrounded the browser suspends/mutes the camera track;
      // that's a tab-switch (handled by the page's tab-switch flow), not the
      // candidate turning the camera off. Don't let it accumulate dead polls.
      // 'ended' tracks (real stop/unplug) persist and are caught on return.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      const stream = typeof getStreamRef.current === 'function' ? getStreamRef.current() : null
      const live = isCameraLive(stream)
      if (live) {
        // Stream may have been re-acquired — re-bind event handlers to the new track.
        if (!detach) detach = attach()
        markRestored()
      } else {
        deadPollsRef.current += 1
        if (deadPollsRef.current >= CONFIRM_POLLS) markLost()
        // A re-acquired-but-different dead track: drop stale handlers.
        if (detach) { detach(); detach = null }
      }
    }, POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      clearInterval(id)
      if (detach) detach()
    }
  }, [enabled, markLost, markRestored])

  return { cameraLost, recheck }
}
